/**
 * Stream B — B97-B102: Reputation Management
 *
 * Review collection campaigns, polling, AI responses, GBP sync.
 * Real apiFetch. CSS vars only.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import * as QRCode from "qrcode";
import { ApiError, apiFetch } from "@/lib/api-client";
import { formatDateTime } from "@/lib/i18n";
import { Star, Send, RefreshCw, Sparkles, MessageSquare, Globe, Plus, X, QrCode, Copy, Printer } from "lucide-react";
import { Skeleton, SkeletonCard } from "@/components/skeleton";

interface ReviewCampaign {
  id: string;
  tenantSlug?: string;
  name: string;
  triggerEvent: string;
  channelPriority: "email" | "sms" | null;
  googleReviewUrl?: string | null;
  facebookReviewUrl?: string | null;
  sentCount: number;
  responseCount: number;
  responseRate: number;
  dailySendLimit: number;
  delayMinutes: number;
  status: "active" | "paused" | "archived";
  createdAt: string;
}

interface ReviewSource {
  id: string;
  platform: string;
  sourceType: string;
  accountId: string;
  accountName: string | null;
  connected: boolean;
  lastSyncAt: string | null;
  reviewCount: number;
  averageRating: number;
}

interface AiDraft {
  reviewId: string;
  reviewerName: string;
  platform: string;
  rating: number;
  reviewText: string;
  draftResponse: string;
}

interface ReviewCampaignExecutionSummary {
  campaignId: string;
  campaignName: string;
  triggerEvent: string;
  queuedCount: number;
  suppressedCount: number;
  triggeredCount: number;
  dailyLimitSkipped: number;
}

type ReviewTouchpointMedium = "qr" | "nfc";

function getReviewTouchpointBaseUrl(): string {
  const pagesUrl = import.meta.env.VITE_PAGES_URL?.trim();
  if (pagesUrl) {
    return pagesUrl.replace(/\/$/, "");
  }
  if (typeof window !== "undefined") {
    return window.location.origin.replace(/\/$/, "");
  }
  return "https://app.stero.ai";
}

function buildReviewTouchpointUrl(
  campaign: ReviewCampaign,
  medium: ReviewTouchpointMedium,
): string {
  const tenantSlug = campaign.tenantSlug?.trim() || "tenant";
  const baseUrl = getReviewTouchpointBaseUrl();
  return `${baseUrl}/review-touchpoints/${encodeURIComponent(tenantSlug)}/${encodeURIComponent(campaign.id)}?medium=${medium}`;
}

function ReviewTouchpointCard({ campaign }: { campaign: ReviewCampaign }) {
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState<ReviewTouchpointMedium | null>(null);
  const hasDestination = Boolean(campaign.googleReviewUrl || campaign.facebookReviewUrl);
  const tenantSlug = campaign.tenantSlug?.trim();
  const qrUrl = useMemo(() => buildReviewTouchpointUrl(campaign, "qr"), [campaign]);
  const nfcUrl = useMemo(() => buildReviewTouchpointUrl(campaign, "nfc"), [campaign]);

  useEffect(() => {
    if (!tenantSlug || !hasDestination) {
      setQrDataUrl(null);
      return;
    }

    let cancelled = false;
    QRCode.toDataURL(qrUrl, {
      margin: 1,
      width: 192,
      color: {
        dark: "#102417",
        light: "#ffffff",
      },
    }).then((dataUrl) => {
      if (!cancelled) {
        setQrDataUrl(dataUrl);
      }
    }).catch(() => {
      if (!cancelled) {
        setQrDataUrl(null);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [hasDestination, qrUrl, tenantSlug]);

  if (!tenantSlug) {
    return null;
  }

  const handleCopy = async (value: string, medium: ReviewTouchpointMedium) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(medium);
      window.setTimeout(() => setCopied(null), 1600);
    } catch {
      setCopied(null);
    }
  };

  const handlePrint = () => {
    if (typeof window !== "undefined") {
      window.print();
    }
  };

  if (!hasDestination) {
    return (
      <div className="mt-4 rounded-xl border border-[var(--color-warning)]/20 bg-[var(--color-warning)]/5 p-3 text-xs text-[var(--color-muted-foreground)]">
        Add a Google or Facebook review URL before printing QR/NFC touchpoints for this campaign.
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)]/60 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 text-sm font-medium text-[var(--color-foreground)]">
            <QrCode className="h-4 w-4 text-[var(--color-primary)]" />
            QR/NFC touchpoint
          </p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            Printable front-desk card plus NFC URL. Scans are tracked as qr.scanned attribution before redirecting to the public review destination.
          </p>
        </div>
        <button
          type="button"
          onClick={handlePrint}
          className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-xs text-[var(--color-foreground)] hover:bg-[var(--color-background)]"
        >
          <Printer className="h-3.5 w-3.5" />
          Print card
        </button>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-[220px_1fr]">
        <div className="rounded-[24px] border border-[var(--color-border)] bg-[var(--color-background)] p-4 text-center shadow-sm">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[var(--color-primary)]">Review us</p>
          <div className="mt-3 flex min-h-[192px] items-center justify-center rounded-2xl bg-white p-3">
            {qrDataUrl ? (
              <img src={qrDataUrl} alt={`QR code for ${campaign.name}`} className="h-48 w-48" />
            ) : (
              <Skeleton className="h-48 w-48 rounded-xl" />
            )}
          </div>
          <p className="mt-3 text-sm font-semibold text-[var(--color-foreground)]">{campaign.name}</p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">Scan after your visit</p>
        </div>

        <div className="space-y-3">
          <label className="block text-xs text-[var(--color-muted-foreground)]">
            <span className="mb-1 block font-medium text-[var(--color-foreground)]">QR URL</span>
            <code className="block break-all rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-[11px] text-[var(--color-muted-foreground)]">
              {qrUrl}
            </code>
          </label>
          <label className="block text-xs text-[var(--color-muted-foreground)]">
            <span className="mb-1 block font-medium text-[var(--color-foreground)]">NFC URL record</span>
            <code className="block break-all rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-[11px] text-[var(--color-muted-foreground)]">
              {nfcUrl}
            </code>
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void handleCopy(qrUrl, "qr")}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-xs text-[var(--color-foreground)] hover:bg-[var(--color-background)]"
            >
              <Copy className="h-3.5 w-3.5" />
              {copied === "qr" ? "Copied QR" : "Copy QR URL"}
            </button>
            <button
              type="button"
              onClick={() => void handleCopy(nfcUrl, "nfc")}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-xs text-[var(--color-foreground)] hover:bg-[var(--color-background)]"
            >
              <Copy className="h-3.5 w-3.5" />
              {copied === "nfc" ? "Copied NFC" : "Copy NFC URL"}
            </button>
          </div>
          <p className="text-xs text-[var(--color-muted-foreground)]">
            NFC chips should be programmed as a URI record with the NFC URL above. The same campaign destination is used for QR and NFC, with medium-specific attribution.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function ReviewCampaignsPage() {
  const [campaigns, setCampaigns] = useState<ReviewCampaign[]>([]);
  const [sources, setSources] = useState<ReviewSource[]>([]);
  const [aiDrafts, setAiDrafts] = useState<AiDraft[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isApproving, setIsApproving] = useState<string | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isExecutingCampaignId, setIsExecutingCampaignId] = useState<string | null>(null);
  const [campaignError, setCampaignError] = useState<string | null>(null);
  const [executionError, setExecutionError] = useState<string | null>(null);
  const [executionSummary, setExecutionSummary] = useState<ReviewCampaignExecutionSummary | null>(null);
  const [form, setForm] = useState({
    name: "",
    triggerEvent: "booking.completed",
    channelPriority: "email",
    googleReviewUrl: "",
    facebookReviewUrl: "",
  });

  const fetchData = useCallback(async () => {
    try {
      const [campRes, srcRes, intelligenceRes] = await Promise.all([
        apiFetch<{ data: ReviewCampaign[] }>("/crm/reviews/campaigns"),
        apiFetch<{ data: ReviewSource[] }>("/crm/reviews/sources"),
        apiFetch<{ data: { aiDrafts: AiDraft[] } }>("/crm/reviews/intelligence"),
      ]);
      setCampaigns(campRes.data);
      setSources(srcRes.data);
      setAiDrafts(intelligenceRes.data.aiDrafts);
    } catch { /* empty states */ }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try { await fetchData(); } finally { setIsRefreshing(false); }
  };

  const handleApproveResponse = async (reviewId: string, response: string) => {
    setIsApproving(reviewId);
    try { await apiFetch("/crm/reviews/respond", { method: "POST", body: JSON.stringify({ reviewId, response }) }); await fetchData(); } catch { /* error */ }
    finally { setIsApproving(null); }
  };

  const handleCreateCampaign = async () => {
    setIsCreating(true);
    setCampaignError(null);
    try {
      await apiFetch("/crm/reviews/campaigns", {
        method: "POST",
        body: JSON.stringify({
          name: form.name.trim(),
          triggerEvent: form.triggerEvent,
          channelPriority: form.channelPriority,
          googleReviewUrl: form.googleReviewUrl.trim() || undefined,
          facebookReviewUrl: form.facebookReviewUrl.trim() || undefined,
        }),
      });
      setForm({
        name: "",
        triggerEvent: "booking.completed",
        channelPriority: "email",
        googleReviewUrl: "",
        facebookReviewUrl: "",
      });
      setIsCreateOpen(false);
      await fetchData();
    } catch {
      setCampaignError("Failed to create review campaign. Add at least one review URL and try again.");
    } finally {
      setIsCreating(false);
    }
  };

  const handleExecuteCampaign = async (campaign: ReviewCampaign) => {
    setIsExecutingCampaignId(campaign.id);
    setExecutionError(null);
    setExecutionSummary(null);

    try {
      const response = await apiFetch<{ data: ReviewCampaignExecutionSummary }>(
        `/crm/reviews/campaigns/${campaign.id}/execute`,
        { method: "POST" },
      );
      setExecutionSummary(response.data);
      await fetchData();
    } catch (error) {
      setExecutionError(error instanceof ApiError ? error.message : "Failed to execute review campaign.");
    } finally {
      setIsExecutingCampaignId(null);
    }
  };

  if (isLoading) return <div className="space-y-4"><Skeleton className="h-8 w-48 rounded" />{[1,2,3].map(i=><SkeletonCard key={i} className="h-24 rounded-xl" />)}</div>;

  return (
    <div className="space-y-8">
        <div className="flex items-center justify-between">
        <div className="flex items-center gap-3"><Star className="h-5 w-5 text-[var(--color-warning)]" /><h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Reputation Management</h1></div>
        <div className="flex gap-2">
          <button type="button" onClick={handleRefresh} disabled={isRefreshing} className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-surface)] disabled:opacity-50">
            {isRefreshing ? (
              <>
                <Skeleton className="h-3.5 w-3.5" />
                <Skeleton className="h-3.5 w-20" />
              </>
            ) : (
              <>
                <RefreshCw className="h-3.5 w-3.5" />
                Refresh
              </>
            )}
          </button>
          <button type="button" onClick={() => setIsCreateOpen(true)} className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-primary)] px-3 py-1.5 text-sm text-white hover:opacity-90"><Plus className="h-3.5 w-3.5" />New Campaign</button>
        </div>
      </div>

      {isCreateOpen && (
        <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-medium text-[var(--color-foreground)]">Create Review Campaign</h2>
            <button type="button" onClick={() => setIsCreateOpen(false)} className="rounded-md p-1 text-[var(--color-muted-foreground)] hover:bg-[var(--color-surface)] hover:text-[var(--color-foreground)]">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="text-sm text-[var(--color-foreground)]">
              <span className="block mb-1">Campaign Name</span>
              <input value={form.name} onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))} className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm" />
            </label>
            <label className="text-sm text-[var(--color-foreground)]">
              <span className="block mb-1">Trigger</span>
              <select value={form.triggerEvent} onChange={(e) => setForm((current) => ({ ...current, triggerEvent: e.target.value }))} className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm">
                <option value="booking.completed">Booking Completed</option>
                <option value="deal.won">Deal Won</option>
                <option value="payment.completed">Payment Completed</option>
                <option value="custom">Custom</option>
              </select>
            </label>
            <label className="text-sm text-[var(--color-foreground)]">
              <span className="block mb-1">Preferred Channel</span>
              <select value={form.channelPriority} onChange={(e) => setForm((current) => ({ ...current, channelPriority: e.target.value }))} className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm">
                <option value="email">Email</option>
                <option value="sms">SMS</option>
              </select>
            </label>
            <label className="text-sm text-[var(--color-foreground)]">
              <span className="block mb-1">Google Review URL</span>
              <input value={form.googleReviewUrl} onChange={(e) => setForm((current) => ({ ...current, googleReviewUrl: e.target.value }))} className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm" />
            </label>
            <label className="text-sm text-[var(--color-foreground)] md:col-span-2">
              <span className="block mb-1">Facebook Review URL</span>
              <input value={form.facebookReviewUrl} onChange={(e) => setForm((current) => ({ ...current, facebookReviewUrl: e.target.value }))} className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm" />
            </label>
          </div>
          {campaignError && <p className="mt-3 text-sm text-[var(--color-error)]">{campaignError}</p>}
          <div className="mt-4 flex items-center justify-between gap-3">
            <p className="text-xs text-[var(--color-muted-foreground)]">Campaign execution remains on the mounted review-request runtime. This surface closes route wiring and real campaign persistence.</p>
            <button type="button" onClick={handleCreateCampaign} disabled={isCreating || form.name.trim().length === 0} className="rounded-lg bg-[var(--color-primary)] px-3 py-2 text-sm text-white hover:opacity-90 disabled:opacity-50">
              {isCreating ? "Creating..." : "Create Campaign"}
            </button>
          </div>
        </section>
      )}

      {/* Connected sources */}
      <section>
        <h2 className="text-lg font-medium text-[var(--color-foreground)] mb-3 flex items-center gap-2"><Globe className="h-4 w-4" />Review Sources</h2>
        <div className="grid gap-3 md:grid-cols-3">
          {sources.map(s => (
            <div key={s.id} className="stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="font-medium text-[var(--color-foreground)]">{s.platform}</p>
                <span className={`h-2 w-2 rounded-full ${s.connected ? "bg-[var(--color-success)]" : "bg-[var(--color-muted-foreground)]"}`} />
              </div>
              {s.accountName && <p className="text-xs text-[var(--color-muted-foreground)] mb-1">{s.accountName}</p>}
              <div className="flex items-center gap-2 text-sm">
                <Star className="h-3.5 w-3.5 text-[var(--color-warning)]" />
                <span style={{fontVariantNumeric:"tabular-nums"}}>{s.averageRating.toFixed(1)}</span>
                <span className="text-[var(--color-muted-foreground)]">({s.reviewCount} reviews)</span>
              </div>
              {s.lastSyncAt && <p className="text-[10px] text-[var(--color-muted-foreground)] mt-1">Synced: {formatDateTime(s.lastSyncAt)}</p>}
            </div>
          ))}
          {sources.length === 0 && <p className="col-span-3 text-sm text-[var(--color-muted-foreground)] text-center py-4">No review sources connected.</p>}
        </div>
      </section>

      {/* AI response drafts */}
      {aiDrafts.length > 0 && (
        <section>
          <h2 className="text-lg font-medium text-[var(--color-foreground)] mb-3 flex items-center gap-2"><Sparkles className="h-4 w-4 text-[var(--color-ai)]" />AI Response Drafts — review before sending</h2>
          <div className="space-y-3">{aiDrafts.map(d => (
            <div key={d.reviewId} className="stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-medium text-[var(--color-foreground)]">{d.reviewerName}</span>
                <span className="text-xs text-[var(--color-muted-foreground)]">{d.platform}</span>
                <div className="flex">{[1,2,3,4,5].map(i => <Star key={i} className={`h-3 w-3 ${i <= d.rating ? "fill-[var(--color-warning)] text-[var(--color-warning)]" : "text-[var(--color-border)]"}`} />)}</div>
              </div>
              <p className="text-sm text-[var(--color-muted-foreground)] italic mb-2">&ldquo;{d.reviewText}&rdquo;</p>
              <div className="rounded-lg bg-[var(--color-ai)]/5 border border-[var(--color-ai)]/20 p-3 mb-2">
                <div className="flex items-center gap-1.5 mb-1"><Sparkles className="h-3.5 w-3.5 text-[var(--color-ai)]" /><span className="text-[10px] font-medium text-[var(--color-ai)]">AI draft</span></div>
                <p className="text-sm text-[var(--color-foreground)]">{d.draftResponse}</p>
              </div>
              <button type="button" onClick={() => handleApproveResponse(d.reviewId, d.draftResponse)} disabled={isApproving === d.reviewId} className="rounded-lg bg-[var(--color-primary)] px-3 py-1.5 text-xs text-white hover:opacity-90 disabled:opacity-50">
                <Send className="inline h-3 w-3 mr-1" />{isApproving === d.reviewId ? "Sending..." : "Approve & Send"}
              </button>
            </div>
          ))}</div>
        </section>
      )}

      {/* Campaigns */}
      <section>
        <h2 className="text-lg font-medium text-[var(--color-foreground)] mb-3 flex items-center gap-2"><MessageSquare className="h-4 w-4" />Review Request Campaigns</h2>
        {executionError && (
          <p className="mb-3 rounded-lg border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 px-3 py-2 text-sm text-[var(--color-error)]">
            {executionError}
          </p>
        )}
        {executionSummary && (
          <p className="mb-3 rounded-lg border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 px-3 py-2 text-sm text-[var(--color-foreground)]">
            Ran {executionSummary.campaignName}: queued {executionSummary.queuedCount}, suppressed {executionSummary.suppressedCount}, triggered {executionSummary.triggeredCount}, daily-limit skipped {executionSummary.dailyLimitSkipped}.
          </p>
        )}
        {campaigns.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-8 text-center text-sm text-[var(--color-muted-foreground)]">No campaigns yet. Create one to start collecting reviews.</div>
        ) : (
          <div className="space-y-2">{campaigns.map(c => (
            <div key={c.id} className="stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="font-medium text-[var(--color-foreground)]">{c.name}</p>
                  <p className="text-xs text-[var(--color-muted-foreground)]">{c.triggerEvent} | {c.channelPriority ?? "auto"} | delay {c.delayMinutes}m | limit {c.dailySendLimit}/day</p>
                </div>
                <div className="text-right">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${c.status === "active" ? "bg-[var(--color-success)]/10 text-[var(--color-success)]" : c.status === "archived" ? "bg-[var(--color-surface)] text-[var(--color-muted-foreground)]" : "bg-[var(--color-warning)]/10 text-[var(--color-warning)]"}`}>{c.status}</span>
                  <p className="text-xs text-[var(--color-muted-foreground)] mt-1" style={{fontVariantNumeric:"tabular-nums"}}>{c.sentCount} sent • {c.responseRate}% response</p>
                  {c.status === "active" && (
                    <button
                      type="button"
                      onClick={() => handleExecuteCampaign(c)}
                      disabled={isExecutingCampaignId === c.id}
                      className="mt-2 rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-xs text-[var(--color-foreground)] hover:bg-[var(--color-surface)] disabled:opacity-50"
                    >
                      {isExecutingCampaignId === c.id ? "Running..." : "Run now"}
                    </button>
                  )}
                </div>
              </div>
              <ReviewTouchpointCard campaign={c} />
            </div>
          ))}</div>
        )}
      </section>
    </div>
  );
}
